// ─── UGC Explorer constants ──────────────────────────────────────────────────
// Central place for anything that varies by platform or content-type/sentiment
// copy, so the page/components/service layer never hardcode these inline.

// Poll interval while a discovery/refresh job is running. The API doc recommends
// polling every ~10s - a first run takes roughly 30s to 3 minutes.
export const POLL_INTERVAL_MS = 10000
// Safety cap so a dead/stuck job doesn't poll forever (~5 minutes of attempts).
export const MAX_POLL_ATTEMPTS = 30

// YouTube's cards are wider (16:9 thumbnails) - 3 fit per row at this grid's
// column widths, so 9 lays out as 3 even rows.
export const DEFAULT_PAGE_SIZE = 9

// Instagram/TikTok cards are the narrower portrait ones (`.ugc-video-grid--reels`
// in UGCExplorer.jsx) - 5 fit per row, so 9 would leave the second row short
// by one. 10 fills exactly 2 full rows on first load.
export const PAGE_SIZE_REELS = 10

export const getPageSize = (platformId) =>
  (platformId === "instagram" || platformId === "tiktok") ? PAGE_SIZE_REELS : DEFAULT_PAGE_SIZE

// Every `sort` value the YouTube backend accepts (RECENT_VIDEO_FILTERS.md §3).
// Aliases like `engagement`/`growth` resolve server-side - the button shows
// the canonical label, and `sorted_by` in the response is the source of
// truth for what actually ranked the page (see the "views_per_day" tooltip
// in SortDropdown usage - likes/comments are null on every video, so
// velocity is the only real interaction signal, never call it "Engagement
// Rate" for this platform).
export const SORT_OPTIONS = [
  { value: "views", label: "Most Viewed" },
  { value: "latest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "trending", label: "Trending" },
  { value: "velocity", label: "Views / Day" },
  { value: "longest", label: "Longest" },
  { value: "shortest", label: "Shortest" },
]

// Instagram has no `trending` sort (there's usually only one stats reading
// per reel with a 3-month refresh, per INSTAGRAM_REEL_API.md §1) but, unlike
// YouTube, likes/comments/shares are really populated - so `engagement` is a
// real ranking here, not an alias for velocity (§2).
export const SORT_OPTIONS_INSTAGRAM = [
  { value: "views", label: "Most Played" },
  { value: "latest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "engagement", label: "Engagement Rate" },
  { value: "likes", label: "Most Liked" },
  { value: "comments", label: "Most Commented" },
  { value: "shares", label: "Most Shared" },
  { value: "velocity", label: "Plays / Day" },
  { value: "longest", label: "Longest" },
  { value: "shortest", label: "Shortest" },
]

// TikTok has real engagement (likes/comments/shares) AND real follower data,
// plus a fifth metric neither sibling reports - `saves`, "the strongest
// intent signal TikTok emits" (TIKTOK_VIDEO_API.md §1/§2). `followers` sorts
// by real audience size, not observed reach - there's no YouTube/Instagram
// equivalent since neither ever gets a real follower count.
export const SORT_OPTIONS_TIKTOK = [
  { value: "views", label: "Most Viewed" },
  { value: "latest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "engagement", label: "Engagement Rate" },
  { value: "likes", label: "Most Liked" },
  { value: "comments", label: "Most Commented" },
  { value: "shares", label: "Most Shared" },
  { value: "saves", label: "Most Saved" },
  { value: "velocity", label: "Views / Day" },
  { value: "followers", label: "Creator Followers" },
  { value: "longest", label: "Longest" },
  { value: "shortest", label: "Shortest" },
]

export const getSortOptions = (platformId) => {
  if (platformId === "instagram") return SORT_OPTIONS_INSTAGRAM
  if (platformId === "tiktok") return SORT_OPTIONS_TIKTOK
  return SORT_OPTIONS
}

export const DURATION_OPTIONS = [
  { value: "short", label: "Short (≤60s)" },
  { value: "medium", label: "Medium (1–10m)" },
  { value: "long", label: "Long (10m+)" },
  { value: "unknown", label: "Unknown" },
]

// Instagram's duration buckets cut at 15s/60s, not YouTube's 60s/10m
// (INSTAGRAM_REEL_API.md §2).
export const DURATION_OPTIONS_INSTAGRAM = [
  { value: "short", label: "Short (≤15s)" },
  { value: "medium", label: "Medium (15–60s)" },
  { value: "long", label: "Long (60s+)" },
  { value: "unknown", label: "Unknown" },
]

// TikTok has a 4th bucket Instagram doesn't - long-form is real here, so
// 60s+ splits into "long" and "extended" at the 180s cut (TIKTOK_VIDEO_API.md
// §1/§2: "15s, 60s and 180s are the cut points").
export const DURATION_OPTIONS_TIKTOK = [
  { value: "short", label: "Short (≤15s)" },
  { value: "medium", label: "Medium (15–60s)" },
  { value: "long", label: "Long (60s–3m)" },
  { value: "extended", label: "Extended (3m+)" },
  { value: "unknown", label: "Unknown" },
]

export const getDurationOptions = (platformId) => {
  if (platformId === "instagram") return DURATION_OPTIONS_INSTAGRAM
  if (platformId === "tiktok") return DURATION_OPTIONS_TIKTOK
  return DURATION_OPTIONS
}

// Views-based reach tiers, not subscribers/followers - see
// RECENT_VIDEO_FILTERS.md §6 (YouTube) and INSTAGRAM_REEL_API.md §5
// (`creator.tier` - "observed reach, not audience size"; `followers` is
// always null for Instagram, nothing in the reel payload reports one).
export const CREATOR_SIZE_OPTIONS = [
  { value: "micro", label: "Micro" },
  { value: "mid", label: "Mid" },
  { value: "major", label: "Major" },
  { value: "mega", label: "Mega" },
  { value: "unknown", label: "Unknown" },
]

// TikTok's `creator_size` is a **real follower band**, not an observed-reach
// proxy (TIKTOK_VIDEO_API.md §2/§8 "Creator tiers") - six tiers instead of
// five, with `nano` (<10k) at the bottom and `major` renamed `macro` to match
// the API's own vocabulary.
export const CREATOR_SIZE_OPTIONS_TIKTOK = [
  { value: "nano", label: "Nano" },
  { value: "micro", label: "Micro" },
  { value: "mid", label: "Mid" },
  { value: "macro", label: "Macro" },
  { value: "mega", label: "Mega" },
  { value: "unknown", label: "Unknown" },
]

export const getCreatorSizeOptions = (platformId) =>
  platformId === "tiktok" ? CREATOR_SIZE_OPTIONS_TIKTOK : CREATOR_SIZE_OPTIONS

// Instagram-only: `audio_type` filter (INSTAGRAM_REEL_API.md §2).
export const AUDIO_TYPE_OPTIONS = [
  { value: "original_sounds", label: "Original audio" },
  { value: "licensed_music", label: "Licensed music" },
]

export const PUBLISHED_WITHIN_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
]

// Empty/default shape for the filter bar - kept here so UGCExplorer.jsx and
// FiltersPanel.jsx agree on the same keys without importing one another.
export const DEFAULT_FILTERS = {
  duration: [],
  contentType: [],
  sentiment: [],
  creatorSize: [],
  channel: "",
  minViews: "",
  publishedWithinDays: "",
  q: "",
  isShort: false,
  isLive: false,
  hasTranscript: false,
}

// Instagram's filter set (INSTAGRAM_REEL_API.md §2) shares duration/content
// type/sentiment/creator size/min views/published-within/q with YouTube's,
// but swaps "channel" for "username" and adds the real engagement metrics
// (likes/comments/shares/engagement rate) YouTube can never report, plus
// audio type, hashtag and a different set of read-only flags.
export const DEFAULT_FILTERS_INSTAGRAM = {
  duration: [],
  contentType: [],
  sentiment: [],
  creatorSize: [],
  audioType: [],
  username: "",
  hashtag: "",
  minViews: "",
  minLikes: "",
  minComments: "",
  minShares: "",
  minEngagementRate: "",
  publishedWithinDays: "",
  q: "",
  isVerified: false,
  hasCaption: false,
  hasHashtags: false,
  mentionsApp: false,
}

// TikTok's filter set (TIKTOK_VIDEO_API.md §2) shares duration/content
// type/sentiment/creator size/username/hashtag/min views/published-within/q
// with Instagram's, but has no `is_verified` (TikTok reports no verified
// badge - use `min_followers` instead), swaps `audio_type` for a free-text
// `music` filter (no original-vs-licensed flag here), and adds `saves` and
// real follower-band filtering neither sibling can offer.
export const DEFAULT_FILTERS_TIKTOK = {
  duration: [],
  contentType: [],
  sentiment: [],
  creatorSize: [],
  username: "",
  hashtag: "",
  music: "",
  keyword: "",
  minViews: "",
  minLikes: "",
  minComments: "",
  minShares: "",
  minSaves: "",
  minEngagementRate: "",
  minFollowers: "",
  maxFollowers: "",
  publishedWithinDays: "",
  q: "",
  hasCaption: false,
  hasHashtags: false,
  hasMusic: false,
  mentionsApp: false,
}

export const getDefaultFilters = (platformId) => {
  if (platformId === "instagram") return DEFAULT_FILTERS_INSTAGRAM
  if (platformId === "tiktok") return DEFAULT_FILTERS_TIKTOK
  return DEFAULT_FILTERS
}

// Chip copy/colors for `summary.content_type`.
export const CONTENT_TYPE_META = {
  review: { label: "Review", color: "#3b82f6" },
  gameplay: { label: "Gameplay", color: "#8b5cf6" },
  tutorial: { label: "Tutorial", color: "#10b981" },
  news: { label: "News", color: "#f59e0b" },
  announcement: { label: "Announcement", color: "#f59e0b" },
  tier_list: { label: "Tier List", color: "#8b5cf6" },
  comparison: { label: "Comparison", color: "#3b82f6" },
  monetisation: { label: "Monetisation", color: "#ef4444" },
  criticism: { label: "Criticism", color: "#ef4444" },
  promotion: { label: "Promotion", color: "#F6255A" },
  other: { label: "Other", color: "#6b7280" },
  unknown: { label: "Unclassified", color: "#6b7280" },
}

// Chip copy/colors for `summary.sentiment`.
export const SENTIMENT_META = {
  positive: { label: "Positive", color: "#10b981" },
  neutral: { label: "Neutral", color: "#6b7280" },
  negative: { label: "Negative", color: "#ef4444" },
  mixed: { label: "Mixed", color: "#f59e0b" },
  unknown: { label: "Unknown", color: "#6b7280" },
}

// ─── Comment sentiment (SENTIMENT_UGC_API.md) ────────────────────────────────
// The doc asks for a ~15s poll. A first run can sit waiting on video
// intelligence for up to 30 minutes (`SENTIMENT_UGC_INTELLIGENCE_WAIT_MINUTES`),
// so the cap is ~45 minutes of attempts rather than the videos' 5.
export const SENTIMENT_POLL_INTERVAL_MS = 15000
export const SENTIMENT_MAX_POLL_ATTEMPTS = 180

// Display order for every distribution bar/legend - positive and negative at
// the two ends, so the bar reads left-to-right from good to bad.
export const SENTIMENT_ORDER = ["positive", "neutral", "mixed", "negative"]

// The order platforms are shown in on the sentiment panel (matches the tabs).
export const SENTIMENT_PLATFORMS = ["youtube", "instagram", "tiktok"]

// `progress[platform]` stages collapsed into the five steps shown to users.
export const SENTIMENT_STEPS = [
  { id: "videos", label: "Videos", stages: ["queued", "waiting_for_videos", "collecting_videos"] },
  { id: "select", label: "Selecting", stages: ["selecting_videos"] },
  { id: "comments", label: "Comments", stages: ["fetching_comments"] },
  { id: "analyse", label: "Analysing", stages: ["analysing"] },
  { id: "done", label: "Done", stages: ["done", "up_to_date"] },
]

export const SENTIMENT_STAGE_LABEL = {
  queued: "Queued",
  waiting_for_videos: "Waiting for video intelligence",
  collecting_videos: "Collecting verified videos",
  selecting_videos: "Picking the top 5 videos",
  fetching_comments: "Fetching comments",
  analysing: "Analysing comments",
  done: "Done",
  up_to_date: "Up to date",
}

export const PURCHASE_INTENT_META = {
  high: { label: "High", level: 3, color: "#10b981" },
  medium: { label: "Medium", level: 2, color: "#f59e0b" },
  low: { label: "Low", level: 1, color: "#ef4444" },
  none: { label: "None", level: 0, color: "#6b7280" },
}

// ─── Platform registry ───────────────────────────────────────────────────────
// When the backend lights up another platform, add its entry here (`endpoint`
// + `enabled: true`) - the tabs, service layer and empty states all read from
// this list, so nothing else needs to change to surface a new platform.
// `itemNoun`/`itemNounPlural` drive the "X video(s) found" style copy so it
// reads naturally for whatever unit of content the platform actually returns.
// `recentAppsFeature` is the feature key each platform's endpoint claims apps
// under (RECENT_APPS_API.md) - YouTube and Instagram keep fully independent
// "recently explored" lists, including independent guest single-app claims
// (INSTAGRAM_REEL_API.md §8).
export const UGC_PLATFORMS = [
  {
    id: "youtube",
    label: "YouTube",
    icon: "fab fa-youtube",
    color: "#FF0000",
    endpoint: "/socials/recent-intelligence/",
    recentAppsFeature: "recent_video_intelligence",
    itemNoun: "video",
    itemNounPlural: "videos",
    enabled: true,
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: "fab fa-instagram",
    color: "#E1306C",
    endpoint: "/instagram/reel-intelligence/",
    recentAppsFeature: "instagram_reels_intelligence",
    itemNoun: "reel",
    itemNounPlural: "reels",
    enabled: true,
  },
  {
    id: "tiktok",
    label: "TikTok",
    icon: "fab fa-tiktok",
    color: "#25F4EE",
    endpoint: "/tiktok/video-intelligence/",
    recentAppsFeature: "tiktok_video_intelligence",
    itemNoun: "video",
    itemNounPlural: "videos",
    enabled: true,
  },
]

export const DEFAULT_PLATFORM = UGC_PLATFORMS.find(p => p.enabled)?.id || "youtube"

export const getPlatformMeta = (platformId) =>
  UGC_PLATFORMS.find(p => p.id === platformId) || UGC_PLATFORMS[0]
